import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { message } from "antd";
import { CALL_FEATCH_API } from "common/API";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import styled from "@emotion/styled";
import { useSelector } from "react-redux";
import { getUserList } from "redux/selectors";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";

const PageBreadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 32px 0;
  font-size: 14px;
  color: #6b7280;
  span.active {
    color: #111827;
    font-weight: 600;
  }
`;

const PageContent = styled.div`
  padding: 28px 32px;
`;

const PageFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 32px;
  border-top: 1px solid #f3f4f6;
`;

const SendFormGrid = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 16px;
  align-items: start;
  margin-bottom: 6px;
`;

const SendLabel = styled.label`
  font-size: 14px;
  color: #374151;
  font-weight: 400;
  padding-top: 10px;
  line-height: 1;
`;

const SendInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
  background: ${({ readOnly }) => (readOnly ? "#f9fafb" : "#fff")};
  transition: border-color 0.15s;
  &:focus {
    border-color: #0d9488;
  }
  &::placeholder {
    color: #9ca3af;
  }
`;

const SendTextarea = styled.textarea`
  width: 100%;
  min-height: 96px;
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  outline: none;
  resize: vertical;
  box-sizing: border-box;
  transition: border-color 0.15s;
  &:focus {
    border-color: #0d9488;
  }
  &::placeholder {
    color: #9ca3af;
  }
`;

export default function SendFundRequestPage() {
    const history = useHistory();
    const record = history.location.state?.state || {};
    const userList = useSelector(getUserList);

    const { key, url } = queryKeys.getMatterById(record?.matterid);

    const { data: matterDataRes } = useFetch(
        key,
        url,
        {
        enabled: !record?.record?.matterid,
        }
    );

    const matterData = matterDataRes?.response?.data;

    const [title, setTitle] = useState(
        record?.client || ""
    );
    const [subject, setSubject] = useState(
        `Fund request for ${record?.matter || record?.client || "Matter"
        }${record?.matterid ? ` (${record.matterid})` : ""
        }`
    );
    const [to, setTo] = useState(
        [...new Set([record?.primarycontactemail, matterData?.responsiblePersonnel?.map((p) => p.emailid)?.join(", ")]
        ?.filter(Boolean))]
        ?.join(", ")
    );
    const [cc, setCc] = useState("");
    const [body, setBody] = useState(
        `Dear Client,
            We hope you are doing well. This email is to inform you that a fund request has been initiated in our system.

            We kindly request you to review the details and process the payment at your earliest convenience. Should you have any questions or need further clarification, please feel free to contact us.

            Please find the fund request details below.
            Request ID: ${record?.id || ""}
            Amount: ${record?.currency || ""} ${record?.amount || ""}
            Due Date: ${record?.duedate
                ? new Date(record.duedate).toLocaleDateString("en-GB")
                : ""
        }
        Kindly process the payment at the earliest.
        Thank you.`
    );
    const findUser = (id) => userList?.find((u) => u?.userid === id);

    const assignedPartner = findUser(record?.useremail);

    const leadAttorneyName = assignedPartner
        ? `${assignedPartner.firstname} ${assignedPartner.lastname}`
        : null;

    const [footer, setFooter] = useState(
        `Regards,\n${leadAttorneyName || ""}`
    );

    const requestType = record?.requesttype || "";
    const matter = record?.matter || "";
    const amount = Number(record?.amount) || 0;
    const currency = record?.currency || "";
    const dueDate = record?.duedate || "";
    const clientName = record?.client || "";

    const [loading, setLoading] = useState(false);

    const handleSendMail = async () => {
        try {
            setLoading(true);
            const payload = { title, subject, to, cc, body, requestType, matter, amount, currency, dueDate, clientName, footer };
            const response = await CALL_FEATCH_API(
                "insyts-legal/sendfundrequestmail",
                "post",
                payload
            );
            if (
                response?.status === 200 ||
                response?.status === "SUCCESS" ||
                response?.code === "SUCCESS"
            ) {
                message.success("Mail sent successfully ✅");
                history.goBack();
            } else {
                message.error(response?.message || "Failed to send mail ❌");
            }
        } catch (err) {
            message.error("Something went wrong while sending mail ❌");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <PageBreadcrumb>
                <span
                    onClick={() => history.goBack()}
                    style={{ cursor: "pointer", color: "#0d9488", fontWeight: 500 }}
                >
                    Back
                </span>
                <NavigateNextIcon fontSize="small" />
                <span className="active">Sending Fund Request</span>
            </PageBreadcrumb>

            <PageContent>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 24 }}>
                    Client: {record?.clientname || record?.client || "–"}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                    <SendFormGrid>
                        <SendLabel>Title:</SendLabel>
                        <SendInput
                            disabled
                            placeholder="Enter title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </SendFormGrid>

                    <SendFormGrid>
                        <SendLabel>Subject:</SendLabel>
                        <SendInput
                            disabled
                            placeholder="Enter subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                        />
                    </SendFormGrid>

                    <SendFormGrid>
                        <SendLabel>To:</SendLabel>
                        <SendInput
                            disabled
                            placeholder="Enter recipient email"
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                        />
                    </SendFormGrid>

                    <SendFormGrid>
                        <SendLabel>CC:</SendLabel>
                        <SendInput
                            placeholder="Enter CC email"
                            value={cc}
                            onChange={(e) => setCc(e.target.value)}
                        />
                    </SendFormGrid>

                    <SendFormGrid>
                        <SendLabel>Body:</SendLabel>
                        <SendTextarea
                            disabled
                            placeholder="Enter email body"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                        />
                    </SendFormGrid>

                    <SendFormGrid>
                        <SendLabel>Footer:</SendLabel>
                        <SendTextarea
                            disabled
                            placeholder="Enter footer"
                            value={footer}
                            onChange={(e) => setFooter(e.target.value)}
                            rows={3}
                        />
                    </SendFormGrid>
                </div>
            </PageContent>

            <PageFooter>
                <ButtonOutlined onClick={() => history.goBack()}>Cancel</ButtonOutlined>
                <ButtonFilled onClick={handleSendMail} disabled={loading}>
                    {loading ? "Sending..." : "Send Mail"}
                </ButtonFilled>
            </PageFooter>
        </div>
    );
}